import React from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { ACCENT, RADIUS, STATUS, SURFACE } from '../../theme/tokens'

const chipSx = { ml: 0.5, typography: 'caption', height: 18 }

const CHIPS = {
    email: {
        ...chipSx,
        background: ACCENT.bg15,
        color: ACCENT.text,
        border: `1px solid ${ACCENT.border30}`,
    },
    mobile: {
        ...chipSx,
        background: STATUS.success.bg,
        color: STATUS.success.fg,
        border: `1px solid ${STATUS.success.border}`,
    },
}

/**
 * The first rows of the file, with the email and mobile columns marked.
 *
 * Long values (links) are cut with an ellipsis at 200px and shown in full on
 * hover, and the header stays in view while the table scrolls inside its
 * box, so a wide file no longer stretches the page.
 */
const CsvPreview = ({ headers, rows, emailColumn, mobileColumn }) => (
    <TableContainer
        component={Paper}
        elevation={0}
        data-testid="csv-preview"
        sx={{
            maxHeight: 320,
            overflow: 'auto',
            borderRadius: `${RADIUS.sm}px`,
            border: `1px solid ${SURFACE.border}`,
        }}
    >
        <Table size="small" stickyHeader>
            <TableHead>
                <TableRow>
                    {headers.map((h) => (
                        <TableCell
                            key={h}
                            // Opaque, or rows show through the sticky header
                            // (the theme's head ground is translucent). The
                            // card, not the raised ground: the orange "email"
                            // chip is 4.35:1 on raised, 4.85:1 here.
                            sx={{
                                backgroundColor: SURFACE.card,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {h}
                            {h === emailColumn && (
                                <Chip
                                    label="email"
                                    size="small"
                                    sx={CHIPS.email}
                                />
                            )}
                            {h === mobileColumn && (
                                <Chip
                                    label="mobile"
                                    size="small"
                                    sx={CHIPS.mobile}
                                />
                            )}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, i) => (
                    <TableRow key={i}>
                        {headers.map((h) => (
                            <TableCell key={h}>
                                <Box
                                    component="span"
                                    title={row[h] ?? ''}
                                    sx={{
                                        display: 'block',
                                        maxWidth: 200,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {row[h]}
                                </Box>
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
)

CsvPreview.propTypes = {
    headers: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    emailColumn: PropTypes.string,
    mobileColumn: PropTypes.string,
}

export default CsvPreview
